import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { Sidebar } from './Sidebar';
import { WorkspaceOption } from '../types';
import { showError } from '../lib/alerts';

interface DashboardShellProps {
  supabase: SupabaseClient;
  session: Session | null;
}

function userDisplayName(session: Session | null) {
  return session?.user.user_metadata?.full_name || session?.user.email || 'You';
}

export function DashboardShell({ supabase, session }: DashboardShellProps) {
  const [membership, setMembership] = useState<WorkspaceOption | null>(null);

  useEffect(() => {
    if (!session) {
      return;
    }

    const userId = session.user.id;

    let cancelled = false;

    async function loadMembership() {
      try {
        const { data: memberRows, error: memberError } = await supabase
          .from('project_members')
          .select('workspace_id, project_id, role')
          .eq('user_id', userId)
          .limit(1);

        if (memberError) throw memberError;

        const first = (memberRows || [])[0] as { workspace_id: string; project_id: string; role: string } | undefined;
        if (!first) {
          if (!cancelled) {
            setMembership(null);
          }
          return;
        }

        const [{ data: projectRows }, { data: workspaceRows }] = await Promise.all([
          supabase.from('projects').select('id, name, workspace_id').eq('id', first.project_id).limit(1),
          supabase.from('workspaces').select('id, name').eq('id', first.workspace_id).limit(1),
        ]);

        if (cancelled) {
          return;
        }

        const project = (projectRows || [])[0] as { id: string; name: string; workspace_id: string } | undefined;
        const workspace = (workspaceRows || [])[0] as { id: string; name: string } | undefined;

        setMembership({
          workspaceId: first.workspace_id,
          workspaceName: workspace?.name || 'Workspace',
          projectId: first.project_id,
          projectName: project?.name || 'Project',
          role: first.role,
        });
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load dashboard shell');
      }
    }

    void loadMembership();

    return () => {
      cancelled = true;
    };
  }, [session, supabase]);

  return (
    <div className="dashboard-shell">
      <Sidebar
        workspaceName={membership?.workspaceName || 'Workspace'}
        projectName={membership?.projectName || 'Project'}
        userName={userDisplayName(session)}
        userRole={membership?.role || 'owner'}
      />

      <div className="dashboard-main">
        <Outlet />
      </div>
    </div>
  );
}